import type { QueryClient } from "@tanstack/react-query"
import {
	clearSession,
	getRefreshToken,
	getStoredUserId,
	reconcileAccount,
} from "@/shared/lib/auth"
import {
	detectPlatform,
	getDeviceId,
	type Platform,
} from "@/shared/lib/platform"
import { resolveTelegramUserId, telegramStore } from "@/shared/lib/telegram"
import type { CachedAuthResult } from "../api/client"
import { userKeys } from "../model/keys"
import type { User } from "../model/types"
import {
	loadQuery,
	nativeCheckQueryOptions,
	telegramCheckQueryOptions,
	userQueryOptions,
} from "./user-query"

export type Resolution =
	| { status: "anonymous" }
	| { status: "resolved"; user: User | null }
	| { status: "rejected" }

export interface Identity {
	resolveId(locationSearch?: Record<string, unknown>): Promise<string | null>
	prepare?(id: string): Promise<void>
	load(
		queryClient: QueryClient,
		id: string,
		version: number,
		fresh: boolean,
	): Promise<User | null>
	reject(queryClient: QueryClient, id: string): Promise<Resolution>
	cache(queryClient: QueryClient, result: CachedAuthResult): Promise<void>
}

async function resolveSessionId(): Promise<string | null> {
	if (!(await getRefreshToken())) return null
	const userId = await getStoredUserId()
	if (userId !== null) return String(userId)
	await clearSession()
	return null
}

async function loadUserByCheck(
	queryClient: QueryClient,
	check: ReturnType<typeof telegramCheckQueryOptions>,
	fresh: boolean,
): Promise<User | null> {
	const userId = await loadQuery(queryClient, check, fresh)
	if (userId === null) return null
	return queryClient.query({ ...userQueryOptions(userId), staleTime: "static" })
}

function cacheCheckedUser(
	queryClient: QueryClient,
	result: CachedAuthResult,
): number | null {
	if (!result.exists) return null
	queryClient.setQueryData(
		userQueryOptions(result.user.id).queryKey,
		result.user,
	)
	return result.user.id
}

async function forgetCheck(
	queryClient: QueryClient,
	id: string,
): Promise<Resolution> {
	queryClient.removeQueries({ queryKey: userKeys.check(id), exact: true })
	return { status: "rejected" }
}

const telegram: Identity = {
	resolveId: async (locationSearch) =>
		resolveTelegramUserId(telegramStore, locationSearch),
	prepare: (id) => reconcileAccount(id),
	load: (queryClient, id, version, fresh) =>
		loadUserByCheck(
			queryClient,
			telegramCheckQueryOptions(queryClient, id, version),
			fresh,
		),
	reject: forgetCheck,
	cache: async (queryClient, result) => {
		const userId = cacheCheckedUser(queryClient, result)
		const id = resolveTelegramUserId(telegramStore)
		if (id) queryClient.setQueryData(userKeys.check(id), userId)
	},
}

const native: Identity = {
	resolveId: async () => {
		const [id] = await Promise.all([resolveSessionId(), getDeviceId()])
		return id
	},
	load: async (queryClient, id, version, fresh) => {
		const deviceId = await getDeviceId()
		return loadUserByCheck(
			queryClient,
			nativeCheckQueryOptions(queryClient, Number(id), deviceId, version),
			fresh,
		)
	},
	reject: forgetCheck,
	cache: async (queryClient, result) => {
		const userId = cacheCheckedUser(queryClient, result)
		const storedUserId = await getStoredUserId()
		if (storedUserId !== null) {
			queryClient.setQueryData(userKeys.check(String(storedUserId)), userId)
			return
		}
		if (userId !== null) {
			queryClient.setQueryData(userKeys.check(String(userId)), userId)
		}
	},
}

const browser: Identity = {
	resolveId: resolveSessionId,
	load: (queryClient, id, _version, fresh) =>
		loadQuery(queryClient, userQueryOptions(Number(id)), fresh),
	reject: async () => {
		await clearSession()
		return { status: "anonymous" }
	},
	cache: async (queryClient, result) => {
		cacheCheckedUser(queryClient, result)
	},
}

const identities: Record<Platform, Identity> = {
	telegram,
	android: native,
	ios: native,
	browser,
}

export function currentIdentity(): Identity {
	return identities[detectPlatform()]
}
